/*    
<summary>
  "ConsumptionTax" page manages consumption tax settings by handling fetch, add/update, and user feedback with toasts.
  Developer: Mohammad Saquib Khan, Lizera Co., Ltd.
</summary>
<returns>JSX</returns>
*/

import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../contexts/store-provider";
import { getHeadquarterIdFromLocalStorage } from "../../helpers/localstorage-helper";
import { IConsumptionTaxState } from "../../models/state/IMasterSettingsState";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import { formatMessage } from "../../translations/format-message";
import AddConsumptionTaxForm from "./forms/add-edit-consumption-tax-form";

const ConsumptionTax = () => {
  const { masterManagementStore } = useStore();
  const {
    UpdateConsumptionTaxService,
    AddConsumptionTaxService,
    GetConsumptionTaxByHeadquarterIdService,
    addUpdateConsumptionTaxState,
    consumptioTaxSetting,
    getConsumptionTaxDetail,
    getByIdConsumptionTaxState,
    resetAddUpdateConsumptionTaxState,
    resetConsumptionTaxSetting,
    disableAction,
    setDisableAction
  } = masterManagementStore;

  const HeadquaterId = getHeadquarterIdFromLocalStorage();
  const [isActuallyUpdateMode, setIsActuallyUpdateMode] = useState(false);

  // Fetch the agreement password for the current store when the component mounts
  useEffect(() => {
    resetAddUpdateConsumptionTaxState();
    resetConsumptionTaxSetting();
    if (HeadquaterId > 0) GetConsumptionTaxByHeadquarterIdService(HeadquaterId);
    return(()=>{
      setDisableAction(false);
    })
  }, []);

  // Update the local state to determine whether we are in "update" mode or "add" mode based on fetched data
  useEffect(() => {
    setIsActuallyUpdateMode((consumptioTaxSetting?.Id ?? 0) > 0);
  }, [consumptioTaxSetting]);

  const isUpdateMode = !!(
    consumptioTaxSetting?.Id && consumptioTaxSetting.Id > 0
  );

  // Show success toast on password add/update and reset the state afterward
  useEffect(() => {
    if (addUpdateConsumptionTaxState.success) {
      toast.success(
        formatMessage(
          isActuallyUpdateMode ? "updated_success" : "added_success"
        )
      );
      if (HeadquaterId > 0)
        GetConsumptionTaxByHeadquarterIdService(HeadquaterId);
      resetAddUpdateConsumptionTaxState();
    }
  }, [addUpdateConsumptionTaxState.success]);

  // Show error toast when password update fails and reset the state afterward
  useEffect(() => {
    if (addUpdateConsumptionTaxState.error) {
      toast.error(<FormattedMessage id={addUpdateConsumptionTaxState.error} />);
    }
  }, [addUpdateConsumptionTaxState.error]);

  // function to handle form submission whether to add or update password
  const submitHandler = (data: IConsumptionTaxState) => {
    const submitData: IConsumptionTaxState = {
      Id: consumptioTaxSetting?.Id ?? 0,
      HeadquarterId: HeadquaterId,
      TaxValue: data.TaxValue,
    };

    if (consumptioTaxSetting?.Id && consumptioTaxSetting.Id > 0) {
      UpdateConsumptionTaxService(consumptioTaxSetting.Id, submitData);
    } else {
      AddConsumptionTaxService(submitData);
    }
  };

  return (
    <>
      {(getByIdConsumptionTaxState.inProgress ||
        addUpdateConsumptionTaxState.inProgress) && (
        <FormLoader
          loading={
            getByIdConsumptionTaxState.inProgress ||
            addUpdateConsumptionTaxState.inProgress
          }
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="consumption_tax" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="various_settings" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="consumption_tax" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row align-items-center justify-content-center">
          <div className="col-md-7 col-lg-6 col-xl-4 col-xxl-3 col-12">
            <div className="card">
              <div className="card-header p-3">
                <h3 className="card-title mb-0">
                  <FormattedMessage id="set_consumption_tax" />
                </h3>
              </div>
              <div className="card-body">
                <AddConsumptionTaxForm
                  id={HeadquaterId}
                  initialValues={getConsumptionTaxDetail}
                  submitHandler={submitHandler}
                  loading={addUpdateConsumptionTaxState.inProgress}
                  isUpdateMode={isUpdateMode}
                  disableAction={disableAction}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(ConsumptionTax);
